'use client'

import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { CheckIcon, DividerHorizontalIcon } from '@radix-ui/react-icons'
import * as React from 'react'

import { cn } from '@/utils/shadcn/utils'

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      `peer shrink-0 h-3.5 w-3.5 rounded-sm border cursor-pointer data-disabled:cursor-not-allowed`,
      // Default, active, light mode
      `border-moment-blue-grey-400`,
      // Default, active, dark mode
      `dark:border-moment-grey-700`,
      // Default, disabled, light mode
      `data-disabled:bg-moment-white-200 data-disabled:border-primary-border`,
      // Default, disabled, dark mode
      `dark:data-disabled:bg-moment-black-200 dark:data-disabled:border-moment-blue-grey-1000`,

      // Hover, active, light mode when not disabled
      `hover:enabled:data-[state=unchecked]:border-moment-teal-400 hover:enabled:data-[state=unchecked]:bg-moment-black-hover`,
      // Hover, active, dark mode when not disabled
      `dark:hover:enabled:data-[state=unchecked]:border-moment-teal-800 dark:hover:enabled:data-[state=unchecked]:bg-moment-white-hover`,

      // Selected, disabled, light mode
      `data-disabled:data-[state=checked]:bg-moment-teal-200 data-disabled:data-[state=checked]:border-primary-border data-disabled:data-[state=checked]:text-moment-teal-400`,
      // Selected, disabled, dark mode
      `dark:data-disabled:data-[state=checked]:bg-moment-teal-1000 dark:data-disabled:data-[state=checked]:text-moment-teal-800`,
      // Indeterminate, disabled, light mode
      `data-disabled:data-[state=indeterminate]:bg-moment-teal-200 data-disabled:data-[state=indeterminate]:border-primary-border data-disabled:data-[state=indeterminate]:text-moment-teal-400`,
      // Indeterminate, disabled, dark mode
      `dark:data-disabled:data-[state=indeterminate]:bg-moment-teal-1000 dark:data-disabled:data-[state=indeterminate]:text-moment-teal-800`,

      // Selected, active, light mode
      `data-[state=checked]:bg-moment-teal-800 data-[state=checked]:border-moment-teal-800 data-[state=checked]:text-moment-white-100`,
      // Selected, active, dark mode
      `data-[state=checked]:bg-moment-teal-800 data-[state=checked]:border-moment-teal-800 data-[state=checked]:text-moment-white-100`,
      // Indeterminate, active, light mode
      `data-[state=indeterminate]:bg-moment-teal-800 data-[state=indeterminate]:border-moment-teal-800 data-[state=indeterminate]:text-moment-white-100`,
      // Indeterminate, active, dark mode
      `data-[state=indeterminate]:bg-moment-teal-800 data-[state=indeterminate]:border-moment-teal-800 data-[state=indeterminate]:text-moment-white-100`,
      className,
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      className={cn(`flex items-center justify-center p-[1px] text-current`)}
    >
      {props.checked === 'indeterminate' && (
        <DividerHorizontalIcon className="h-full w-full" />
      )}
      {props.checked === true && <CheckIcon className="h-full w-full" />}
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
))
Checkbox.displayName = CheckboxPrimitive.Root.displayName

export { Checkbox }
